<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTypedStore } from '@/store';
import { useLodash } from '@/setup/lodash';
import { useDashboardFilter } from '@/composables/useDashboardFilter';
import AddButton from '@/components/AddButton.vue';
import DashboardFilter, { type CurrentFilter } from '@/views/Dashboard/components/DashboardFilter.vue';
import SelectMetrics from '@/views/Dashboard/components/SelectMetrics.vue';
import { ADD_DASHBOARD_IS_LOADING } from '@/store/action_types/dashboards';
import type { DashboardsRequestSeries, DashboardsRequestSeriesItem } from '@/types/dashboard';
import type { FilterRecord, IconControlItem } from '@/types/core';

//#endregion

//#region types
export interface Props {
  disabled?: boolean,
  series?: DashboardsRequestSeries,
  errors?: any,
  propCurrencyCode?: string,
}

export interface Emits {
  'updateSeries': [value: DashboardsRequestSeries],
  'isDisableSelectFilter': [value: boolean]
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  series: () => [],
  errors: () => ({}),
  propCurrencyCode: '',
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const { t } = useI18n();
const store = useTypedStore();
const _ = useLodash();
const {
  decodeFilterSelect,
  transformForFilterView,
  transformFromFilterView,
  parseFilter,
} = useDashboardFilter();
//#endregion

//#region state
const currentSeries = ref(computedSeriesFilter());
const currentSearch = ref(ifEditCurrentSearch());
const isDisabledArray = ref<boolean[]>([]);

const defaultSerie = ref({
  name: t('dashboards.dashboards_filters.new_series'),
  y_axis: 'left',
  metric_name: '' as DashboardsRequestSeriesItem['metric_name'],
  filters: [],
} as DashboardsRequestSeriesItem);
//#endregion

//#region computed
const filterSeries = computed(() => {
  return currentSeries.value.map((series, index) => ({
    ...parseFilter(transformFromFilterView(series.filters as CurrentFilter[])),
    search: currentSearch.value[index] as NonNullable<DashboardsRequestSeriesItem['filters']>['search'],
  }));
});

const paramsData = computed<DashboardsRequestSeries>(() => {
  let result = _.cloneDeep(currentSeries.value);
  result = result.map((series, index) => {
    series['filters'] = filterSeries.value[index]!;
    return series;
  });

  return result;
});

const controlsData = computed<IconControlItem[]>(() => {
  const maxSeries = 20;
  return [
    {
      title: t('dashboards.dashboards_filters.tooltip_copy'),
      icon: 'clone',
      color: 'var(--text-primary)',
      substyle: 'far',
      disabled: currentSeries.value.length >= maxSeries || props.disabled,
      clickHandler: (index: number) => {
        const cloneObj = _.cloneDeep(currentSeries.value[index]!);
        const currSearch = _.cloneDeep(currentSearch.value[index]!);
        currentSeries.value.push(cloneObj);
        currentSearch.value.push(currSearch);
        emit('updateSeries', paramsData.value);
      },
    },
    {
      title: t('dashboards.dashboards_filters.tooltip_delete'),
      color: 'var(--text-negative)',
      icon: 'trash-alt',
      disabled: currentSeries.value.length <= 1 || props.disabled,
      clickHandler: (index: number) => {
        currentSeries.value.splice(index, 1);
        currentSearch.value.splice(index, 1);
        emit('updateSeries', paramsData.value);
      },
    },
  ];
});

const isDisabled = computed(() => isDisabledArray.value.some((item) => item));
//#endregion

//#region methods
function ifEditCurrentSearch() {
  return (props.series || []).map((s) => {
    if (s.filters?.search) {
      return s.filters.search;
    }
    return {} as FilterRecord;
  });
}

function computedSeriesFilter() {
  return props.series.map((s) => {
    return {
      ...s,
      filters: transformForFilterView(decodeFilterSelect(s.filters!)),
    };
  }) as DashboardsRequestSeries;
}

function setNameSeriesMain(name: string, index: number) {
  store.commit(`dashboards/${ADD_DASHBOARD_IS_LOADING}`, true);
  setNameSeries(name, index);
}

const setNameSeries = _.debounce(function setNameSeries(name: string, index: number) {
  currentSeries.value[index]!['name'] = name;
  emit('updateSeries', paramsData.value);
}, 700);

function handleSelectMetric(value: string, index: number) {
  currentSeries.value[index]!['metric_name'] = value as DashboardsRequestSeriesItem['metric_name'];
  emit('updateSeries', paramsData.value);
}

function addSeries() {
  currentSeries.value[currentSeries.value.length] = _.cloneDeep(defaultSerie.value);
  nextTick(() => {
    emit('updateSeries', paramsData.value);
  });
}

function handleChangeFilter() {
  emit('updateSeries', paramsData.value);
}

function handleDisableSelectFilter(disable: boolean, index: number) {
  isDisabledArray.value[index] = disable;
  emit('isDisableSelectFilter', isDisabled.value);
}

function handleChangeFilterTab(search: FilterRecord, index: number) {
  currentSearch.value[index] = _.cloneDeep(search);
  emit('updateSeries', paramsData.value);
}
//#endregion
</script>

<template>
  <div class="dasboard-filter dashboard-series">
    <UiTitle
      :font-size="16"
      class="t:mb-8 t:font-medium"
    >
      {{ $t('dashboards.dashboards_filters.series') }}
    </UiTitle>
    <FormRow
      v-for="(currSeries, index) in currentSeries"
      :key="index"
      class="t:mb-16 t:space-y-16"
    >
      <UiInput
        :label="$t('dashboards.dashboards_filters.label_series')"
        autosize
        size="big"
        :placeholder="$t('dashboards.dashboards_filters.series_placeholder')"
        class="form-input"
        type="text"
        :model-value="currSeries?.name"
        :error="errors[index]?.name"
        @update:model-value="setNameSeriesMain($event, index)"
      />

      <div class="t:flex t:items-end t:justify-between">
        <SelectMetrics
          :label="$t('dashboards.popup_column.metrics')"
          :model-value="currSeries?.metric_name || null"
          @update:model-value="handleSelectMetric($event!, index)"
        />
        <div
          class="t:flex t:space-x-12 t:ml-8 t:mb-10"
        >
          <ActionIcon
            v-for="control in controlsData"
            :key="control.title"
            :icon="control.icon"
            :color="control.color"
            :tooltip="control.title"
            :is-overlay="false"
            :disabled="control.disabled"
            @click="control.clickHandler(index)"
          />
        </div>
      </div>
      <DashboardFilter
        v-model="currentSeries[index]!.filters! as CurrentFilter[]"
        :set-current-search="currentSearch[index]!"
        @change-filter="handleChangeFilter"
        @change-filter-tab="(search) => handleChangeFilterTab(search, index)"
        @is-disable-select-filter="handleDisableSelectFilter($event, index)"
      />
    </FormRow>
    <AddButton
      v-if="currentSeries.length < 20"
      :is-disabled="disabled"
      @click="addSeries"
    >
      {{ $t('dashboards.dashboards_filters.add_series') }}
    </AddButton>
  </div>
</template>
